import type { Filter } from '#layers/data-model/public';

/**
 * The part of an RStore collection this helper touches.
 *
 * Declared with method syntax on purpose: TypeScript checks method parameters bivariantly, so the
 * narrow `peekMany` options below still accept a collection whose own signature is wider
 * (`FindManyOptions`). Written as arrow properties this would not be assignable.
 */
interface JunctionCollection<TItem> {
	peekMany: (options: { filter: Filter }) => TItem[];
	getKey: (item: TItem) => string | number | null | undefined;
	clearItem: (key: string | number) => void;
}

interface DisconnectJunctionAssignmentsOptions<TItem> {
	/** Collection holding the junction rows that link an owner to its assignments. */
	junctionCollection: JunctionCollection<TItem>;
	/** Matches the junction rows the detach removes. */
	filter: Filter;
	/** Performs the detach itself. */
	disconnect: () => Promise<unknown>;
}

/**
 * Detaches assignments, then drops the junction rows the server deleted from the cache.
 *
 * Order matters twice, which is the whole reason this is shared rather than written per caller:
 * the rows are read *before* the detach, because afterwards they no longer match the filter; and
 * they are evicted only *after* it succeeds, so a failed request leaves the cache still describing
 * what the server still has.
 */
export async function disconnectJunctionAssignments<TItem>(
	options: DisconnectJunctionAssignmentsOptions<TItem>,
): Promise<void> {
	const { junctionCollection, filter, disconnect } = options;
	const junctionItems = junctionCollection.peekMany({ filter });

	await disconnect();

	for (const item of junctionItems) {
		const key = junctionCollection.getKey(item);
		if (key != null) junctionCollection.clearItem(key);
	}
}
